import React, { useState, useEffect, useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Tabs,
  TabsList,
  TabsTrigger,
  Button,
  DataTable,
  Badge,
  SpinnerLoader,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import { getBackfillJobsByOperation, BackfillJob } from "../../api";

const formatColumnHeader = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const formatDate = (value: string | null | undefined) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    return d.toLocaleString();
  } catch {
    return String(value);
  }
};

interface LogsDialogProps {
  open: boolean;
  onClose: () => void;
  importId?: string;
}

const LogsDialog: React.FC<LogsDialogProps> = ({
  open,
  onClose,
  importId,
}) => {
  const [jobs, setJobs] = useState<BackfillJob[]>([]);
  const [selectedTab, setSelectedTab] = useState<"ALL" | string>("ALL");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && importId) {
      const operationId = Number(importId);
      const skip = page * rowsPerPage;
      const limit = rowsPerPage;
      setLoading(true);
      getBackfillJobsByOperation(operationId, skip, limit)
        .then(({ items, total_count }) => {
          setJobs(items);
          setTotalCount(total_count);
        })
        .catch((err) => console.error("Error fetching jobs", err))
        .finally(() => setLoading(false));
    }
  }, [open, importId, page, rowsPerPage]);

  const getStatusBadge = (status: string) => {
    const isSuccess = status === "Success";
    const isFailed = status === "Failed";
    const isPending =
      status === "Scheduled" ||
      status === "Running" ||
      status === "Cancelled" ||
      status === "Discarded" ||
      status === "Stale";
    const className = isSuccess
      ? "bg-green-100 text-green-800 border-green-300"
      : isFailed
        ? "bg-red-100 text-red-800 border-red-300"
        : isPending
          ? "bg-amber-100 text-amber-800 border-amber-300"
          : "";
    return (
      <Badge variant="outline" className={className}>
        {status}
      </Badge>
    );
  };

  const dataKeys: (keyof BackfillJob)[] = [
    "operation_id",
    "job_id",
    "job_url",
    "media_platform_account_id",
    "datastream_id",
    "datastream_type_id",
    "job_start",
    "job_end",
    "fetch_period_start",
    "fetch_period_end",
    "progress",
    "created_at",
    "updated_at",
    "account_name",
    "formal_name",
  ];

  const dataTableColumns = useMemo((): TableColumn<BackfillJob>[] => {
    const dateKeys = ["job_start", "job_end", "fetch_period_start", "fetch_period_end", "created_at", "updated_at"];
    const cols: TableColumn<BackfillJob>[] = dataKeys.map((key) => ({
      key,
      header: formatColumnHeader(key),
      accessor: (row) => row[key],
      width: key === "job_url" ? "18%" : "10%",
      render: (value) =>
        dateKeys.includes(key)
          ? formatDate(value as string)
          : String(value ?? ""),
    }));
    cols.push({
      key: "status",
      header: "Status",
      accessor: (row) => row.status,
      width: "10%",
      align: "center",
      render: (_v, row) => (
        <div className="flex justify-center">{getStatusBadge(row.status)}</div>
      ),
    });
    return cols;
  }, []);

  const tabs = useMemo(
    () => ["ALL", ...Array.from(new Set(jobs.map((j) => j.status)))],
    [jobs]
  );
  const filteredJobs =
    selectedTab === "ALL" ? jobs : jobs.filter((j) => j.status === selectedTab);

  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent
        side="right"
        className="w-[900px] sm:max-w-[900px] flex flex-col p-0 gap-0"
      >
        <SheetHeader className="px-6 py-4 border-b flex-shrink-0">
          <SheetTitle>Jobs Detail</SheetTitle>
        </SheetHeader>

        <div className="border-b px-6 pb-3 pt-2 flex-shrink-0">
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList className="inline-flex flex-wrap gap-1 w-fit max-w-full h-auto p-1">
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab}
                  value={tab}
                  className="px-3 py-1.5 text-sm"
                >
                  {tab}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden px-6 flex flex-col">
          <DataTable<BackfillJob>
            className="flex-1 min-h-0 overflow-auto"
            maxHeight="100%"
            columns={dataTableColumns}
            data={filteredJobs}
            loading={loading}
            emptyMessage="No records found."
            stickyHeader
            stickyFooter
            showActions={false}
            pagination={{
              currentPage: page + 1,
              totalPages,
              pageSize: rowsPerPage,
              totalItems: totalCount,
              onPageChange: (p) => setPage(p - 1),
              onPageSizeChange: (size) => {
                setRowsPerPage(size);
                setPage(0);
              },
              pageSizeOptions: [10, 25, 50],
            }}
          />
        </div>

        <SheetFooter className="border-t px-6 py-3 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default LogsDialog;
